import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import {
    createColumnHelper,
    getCoreRowModel,
    useReactTable,
    SortingState,
    getSortedRowModel,
    Row as RowType,
} from '@tanstack/react-table';
import { Link, useNavigate, useParams } from 'react-router-dom-v5-compat';
import { L } from '../../../../lib/i18n';
import FilterSearchContainer from '../../../binders/components/emotion/Filters/FilterSearchContainer';
import GlobalFilter from '../../../../components/Table/FilterComponents/GlobalFilterTable';
import useHandleSearch from '../../hooks/useHandleSearch';
import triggerDirtyFieldsToast from '../../../../components/Forms/DirtyFieldsToast';
import EmptyState from '../../../../components/EmptyStateCommunity/EmptyStateCommunity';
import { Button, ToggleView, Card } from '@ourliving/ourliving-ui';
import { Icon } from '../../../../components/Icon/Icon';
import { useListContentNewsItems } from '../../hooks/ContentNews/contentNewsQueries';
import GenericTableVirtualized from '../../../binders/components/GenericTableVirtualizedInfinite';
import useRemoveContentNews from '../../hooks/ContentNews/useRemoveContentNews';
import { RouterOutput } from '../../../../lib/trpc';
import { RawHtml } from '../../../../components/Layout/RawHtml';
import useGetSignedUrlById from '../../../../lib/hooks/useGetSignedUrlById';
import { useLocalStorage } from 'react-use';

export type FiltersSpaces = {
    space_type: { value: string; label: string }[];
};

const CellContainer = styled.div({
    display: 'grid',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    maxWidth: '100%',
    fontSize: '0.75rem',
    gap: '0.2rem',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
});

const Flex = styled.div({
    display: 'flex',
});

const Data = styled.p({
    margin: '0px',
    textAlign: 'left',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
});

type ContentNewsItem = RouterOutput['ContentNewsApi']['listContentNewsItems']['items']['_']['result'][0];

const columnHelper = createColumnHelper<ContentNewsItem>();

type Props = {
    handleSetNumberOfItems: (val: number | null) => void;
};

const ContentNewsList = ({ handleSetNumberOfItems }: Props) => {
    const { id } = useParams<{ id: string }>();
    return id ? <ContentNews id={+id} handleSetNumberOfItems={handleSetNumberOfItems} /> : null;
};

const NewsCard = ({
    imageId,
    id,
    title,
    description,
    onClick,
}: {
    imageId?: string;
    id: number;
    title?: string;
    description?: string;
    onClick: () => void;
}) => {
    const url = useGetSignedUrlById(imageId, '800x0');

    return (
        <Card key={id} onClick={onClick}>
            <Card.Image src={url?.data} />
            {title && (
                <Card.Title>
                    <RawHtml content={title} />
                </Card.Title>
            )}

            {description && (
                <Card.Description>
                    <RawHtml content={description} />
                </Card.Description>
            )}
        </Card>
    );
};

const ContentNews = ({
    id,
    handleSetNumberOfItems,
}: {
    id: number;
    handleSetNumberOfItems: (val: number | null) => void;
}) => {
    const { handleSearch, searchField } = useHandleSearch();
    const navigate = useNavigate();

    const [sorting, setSorting] = useState<SortingState>([]);
    const [isCardView, setIsCardView] = useLocalStorage<'list' | 'card'>('newsView', 'list');

    const { fetchNextPage, hasNextPage, isFetching, data } = useListContentNewsItems({
        templateId: +id,
        search: searchField ?? '',
    });

    const { mutate: removeContentNews } = useRemoveContentNews();

    const flatData = useMemo(() => data?.pages?.flatMap((page) => page.items) ?? [], [data]);

    const totalDBRowCount = useMemo(() => Number(data?.pages?.[0]?.totalItems ?? 0), [data]);
    const totalFetched = useMemo(() => flatData.length, [flatData]);

    const handleClickRow = useCallback((row: RowType<ContentNewsItem>) => {
        navigate(`${row.original.id}`);
    }, []);

    const handleClickCard = (cardId: number) => {
        navigate(`${cardId}`);
    };

    useEffect(() => {
        handleSetNumberOfItems(data ? data.pages[0].totalItems : null);
    }, [data]);

    const columns = useMemo(
        () => [
            columnHelper.accessor((row) => row.title, {
                id: 'title',
                cell: (info) => (
                    <CellContainer>
                        <Data>{info.getValue()}</Data>
                    </CellContainer>
                ),
                header: () => <Flex>{L('title')}</Flex>,
                minSize: 110,
                size: 150,
                meta: {
                    styles: {
                        flex: '1 0 auto',
                    },
                },
            }),
            columnHelper.accessor((row) => row.locked, {
                id: 'locked',
                cell: (info) => (
                    <CellContainer>
                        <Data>{info.getValue()}</Data>
                    </CellContainer>
                ),
                header: () => <Flex>{L('board_room')}</Flex>,
                minSize: 110,
                size: 150,
                meta: {
                    styles: {
                        flex: '1 0 auto',
                    },
                },
            }),
            columnHelper.display({
                id: 'remove_content_news',
                cell: (info) => (
                    <CellContainer style={{ width: '100%', justifyContent: 'end' }}>
                        <Data
                            onClick={(e) => {
                                e.stopPropagation();
                                return triggerDirtyFieldsToast({
                                    title: `${L('confirmation_content_news_delete')} ${info.row.original.title}`,
                                    continueButtonText: L('yes'),
                                    continueFn: () => {
                                        removeContentNews({ contentNewsId: info.row.original.id, projectId: id });
                                    },
                                    id: info.row.original.id.toString(),
                                });
                            }}
                        >
                            <Icon.TrashCan style={{ alignSelf: 'center' }} />
                        </Data>
                    </CellContainer>
                ),
                minSize: 50,
                size: 50,
                enableResizing: false,
                meta: {
                    styles: {
                        flexShrink: 0,
                    },
                },
            }),
        ],
        [],
    );

    const table = useReactTable({
        data: flatData ?? [],
        columns,
        state: {
            sorting,
        },
        onSortingChange: setSorting,
        sortDescFirst: false,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        columnResizeMode: 'onChange',
        enableColumnResizing: true,
    });

    return (
        <>
            <FilterSearchContainer>
                <div
                    style={{
                        display: 'flex',
                        gap: '1rem',
                        marginLeft: 'auto',
                        paddingBottom: '2rem',
                    }}
                >
                    <GlobalFilter
                        style={{ height: '2.2rem' }}
                        filter={searchField ?? ''}
                        setFilter={handleSearch}
                        placeholder={L('search')}
                    />
                    <ToggleView style={{ width: '2rem' }} view={isCardView || 'list'} setView={setIsCardView} />
                </div>
            </FilterSearchContainer>

            {isCardView === 'card' ? (
                <Card.Grid>
                    {flatData.map((data) => {
                        const hasSpaceInFilename =
                            data?.cover_image_data?.original?.metadata?.filename?.includes(' ') ?? false;
                        return (
                            <NewsCard
                                key={data?.id}
                                id={data.id}
                                title={data.title}
                                description={data.description}
                                imageId={hasSpaceInFilename ? undefined : data?.cover_image_data?.original?.id}
                                onClick={() => handleClickCard(data?.id)}
                            />
                        );
                    })}
                </Card.Grid>
            ) : (
                <GenericTableVirtualized
                    table={table}
                    handleFetchNextPage={() => fetchNextPage()}
                    hasNextPage={hasNextPage}
                    isFetching={isFetching}
                    totalDBRowCount={totalDBRowCount}
                    totalFetched={totalFetched}
                    estimateSize={50}
                    rowClickHandler={handleClickRow}
                />
            )}

            {flatData?.length === 0 &&
                (!searchField ? (
                    <EmptyState>
                        <EmptyState.Title>{L('no_content_news')}</EmptyState.Title>
                        <EmptyState.Description>
                            <p>{L('no_content_news_description')}</p>
                            <p>{L('click_button_below')}</p>
                            <Link to="new">
                                <Button>{L('new_content_news')}</Button>
                            </Link>
                        </EmptyState.Description>
                    </EmptyState>
                ) : (
                    <EmptyState>
                        <EmptyState.Title>{L('no_content_news_found')}</EmptyState.Title>
                        <EmptyState.Description>
                            <p>{L('no_content_news_found_description')}</p>
                            <p>{L('please_try_something_else')}</p>
                        </EmptyState.Description>
                    </EmptyState>
                ))}
        </>
    );
};

export default ContentNewsList;
